interface FilterOptions {
  inputValue: string;
}

export type FilterFunction<T> = (options: T[], state: FilterOptions) => T[];
export type OptionEqualityFunction<T> = (option: T, value: T) => boolean;

export const createAutocompleteFieldFilter =
  <T extends Record<string, any>>(fieldName: keyof T): FilterFunction<T> =>
  (options: T[], { inputValue }: FilterOptions): T[] => {
    const search = (inputValue ?? "").trim().toLowerCase();
    if (!search) {
      return options;
    }

    return (options ?? []).filter((option) => {
      const fieldValue = option?.[fieldName];
      if (fieldValue === undefined || fieldValue === null) {
        return false;
      }

      return String(fieldValue).toLowerCase().includes(search);
    });
  };

export const createAutocompleteFieldFilterWithFallback =
  <T extends Record<string, any>>(
    primaryField: keyof T,
    fallbackField: keyof T,
  ): FilterFunction<T> => {
    const primaryFilter = createAutocompleteFieldFilter(primaryField);
    const fallbackFilter = createAutocompleteFieldFilter(fallbackField);

    return (options: T[], state: FilterOptions): T[] => {
      const hasPrimaryField = (options ?? []).some(
        (option) => option?.[primaryField] !== undefined && option?.[primaryField] !== null,
      );

      return hasPrimaryField ? primaryFilter(options, state) : fallbackFilter(options, state);
    };
  };

export const isOptionEqualById = <T extends { id?: number }>(
  option: T,
  value: T,
): boolean => option?.id === value?.id;

export const isOptionEqualByField =
  <T extends Record<string, any>>(fieldName: keyof T): OptionEqualityFunction<T> =>
  (option: T, value: T): boolean =>
    option?.[fieldName] === value?.[fieldName];

export const getAutocompleteNoOptionsText = (
  entityName: string,
  isLoading: boolean = false,
): string => (isLoading ? `Loading ${entityName}...` : `No ${entityName} found`);
